import { Modal } from '@mantine/core';

import {
    CompanySubscription,
    CreditSubscription,
    DesignerSubscription,
    SubscriptionBillingCycle,
    SubscriptionFromSubscriptionType,
    SubscriptionType,
} from 'models';

import { useCurrentTeam } from 'hooks/use-current-team';
import { useCurrentUser } from 'hooks/use-current-user';
import { PermissionServiceSubscription } from 'services/PermissionServiceSubscription';

import { SubscriptionUpdateCommonPropsWithoutDefaults } from '../types';
import { getSubscriptionMeta } from './helpers/get-subscription-meta';
import { Resubscribe } from './modal-contents/Resubscribe';
import { SubscribeForCompany } from './modal-contents/SubscribeForCompany';
import { SubscribeForCredits } from './modal-contents/SubscribeForCredits';
import { SubscribeForDesigner } from './modal-contents/SubscribeForDesigner';
import { SubscribeNotAllowed } from './modal-contents/SubscribeNotAllowed';

type SubscriptionAndType<T extends SubscriptionType> = {
    type: T;
    toSubscription: SubscriptionFromSubscriptionType<T>;
};

type SubscriptionModalProps<T extends SubscriptionType> = {
    open: boolean;
    onClose: () => void;
} & SubscriptionAndType<T> &
    SubscriptionUpdateCommonPropsWithoutDefaults;

export const SubscriptionModal = <T extends SubscriptionType>({
    open,
    onClose,
    ...props
}: {
    open: boolean;
    onClose: () => void;
    type: T;
    toSubscription: SubscriptionFromSubscriptionType<T>;
} & SubscriptionModalProps<T>) => {
    return (
        <Modal.Root opened={open} onClose={onClose} size="lg" centered zIndex={10000}>
            <Modal.Overlay />
            <Modal.Content>
                <ContentsForType {...props} onClose={onClose} />
            </Modal.Content>
        </Modal.Root>
    );
};

const ContentsForType = <T extends SubscriptionType>({
    type,
    toSubscription,
    team,
    user,
    ...subscriptionProps
}: SubscriptionAndType<T> & SubscriptionUpdateCommonPropsWithoutDefaults & { onClose: () => void }) => {
    const currentTeam = useCurrentTeam();
    const currentUser = useCurrentUser();

    const teamToUpgrade = team ?? currentTeam;
    const userUpgrading = user ?? currentUser;

    if (!teamToUpgrade || !userUpgrading) {
        return <SubscribeNotAllowed />;
    }

    const canPurchase = PermissionServiceSubscription.canPurchaseSubscription(userUpgrading, teamToUpgrade);

    if (!canPurchase) {
        return <SubscribeNotAllowed />;
    }

    const defaultBillingCycle =
        teamToUpgrade.subscriptionBillingCycle ??
        subscriptionProps.defaultBillingCycle ??
        SubscriptionBillingCycle.MONTHLY;

    const { isBeingCancelled } = getSubscriptionMeta(teamToUpgrade);

    const isCurrentSubscriptionBeingCancelled = isBeingCancelled(type);

    if (isCurrentSubscriptionBeingCancelled) {
        return (
            <Resubscribe
                team={teamToUpgrade}
                user={userUpgrading}
                defaultBillingCycle={defaultBillingCycle}
                {...subscriptionProps}
            />
        );
    }

    switch (type) {
        case SubscriptionType.COMPANY:
            return (
                <SubscribeForCompany
                    toSubscription={toSubscription as CompanySubscription}
                    team={teamToUpgrade}
                    user={userUpgrading}
                    defaultBillingCycle={defaultBillingCycle}
                    {...subscriptionProps}
                />
            );

        case SubscriptionType.DESIGNER:
            return (
                <SubscribeForDesigner
                    toSubscription={toSubscription as DesignerSubscription}
                    team={teamToUpgrade}
                    user={userUpgrading}
                    defaultBillingCycle={defaultBillingCycle}
                    {...subscriptionProps}
                />
            );

        case SubscriptionType.CREDIT:
            return (
                <SubscribeForCredits
                    toSubscription={toSubscription as CreditSubscription}
                    team={teamToUpgrade}
                    user={userUpgrading}
                    defaultBillingCycle={defaultBillingCycle}
                    {...subscriptionProps}
                />
            );
    }
};
